import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { StaffNodeData, WorkflowActor } from "@/lib/productGraph";

import { WizardFieldListEditor } from "../wizard-field-list-editor";

interface Props {
  data: StaffNodeData;
  onChange: (patch: Partial<StaffNodeData>) => void;
  showFields?: boolean;
}

const ACTORS: WorkflowActor[] = ["tenant", "tenant_admin", "client"];

export function StaffEditor({ data, onChange, showFields }: Props): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">{t("enterpriseProductBuilder.nodeLabelLabel")}</Label>
        <Input
          value={data.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeDescriptionLabel")}
        </Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.staffActorLabel")}
          </Label>
          <Select
            value={data.actor}
            onValueChange={(v) => onChange({ actor: v as WorkflowActor })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTORS.map((a) => (
                <SelectItem key={a} value={a}>
                  {t(`enterpriseProductBuilder.staffActor.${a}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.staffSlaLabel")}
          </Label>
          <Input
            type="number"
            min={0}
            value={data.slaHours ?? 0}
            onChange={(e) => onChange({ slaHours: Number(e.target.value) })}
          />
        </div>
      </div>

      {showFields && (
        <WizardFieldListEditor
          fields={data.fields ?? []}
          onChange={(fields) => onChange({ fields })}
        />
      )}
    </div>
  );
}
